import { IApiClient } from "../api-client";
import { cacheService, EnvType } from "../services";
import { CreateAsperaTransferRequest } from "../Types/create-aspera-transfer-request";
import { FileDownloadAsperaResponse } from "../Types/file-download-aspera-response";
import { FileInfosResponse } from "../Types/file-infos-response";
import { FileTransfersResponse } from "../Types/file-transfers-response";
import { FileUploadAsperaResponse } from "../Types/file-upload-aspera-response";
import { GenericResponseStd } from "../Types/generic-response-std";
import GetFolderOrAssetSizeResponse from "../Types/get-folder-or-asset-size-response";
import { GetOrderCheckoutAttachedFileInfosResponse } from "../Types/get-order-checkout-attached-file-infos-response";
import { GetOrderCheckoutAttachedFileTypesResponse } from "../Types/get-order-checkout-attached-file-types-response";
import { GetPathFileListRequest } from "../Types/get-path-file-list-request";
import { GetStorageFileListResponse } from "../Types/get-storage-file-list-response";
import { PlatformDocumentsResponse } from "../Types/platform-documents-response";
import { StorageKind } from "../Types/storage-kind";
import { UpdateTransferRequest } from "../Types/update-transfer-request";
import { UploadPlatformDocumentRequest } from "../Types/upload-platform-document-request";
import { DownloadHttpZipFileRequest } from "../Types/download-http-zip-file-request";
import { AxiosResponse, AxiosResponseHeaders } from "axios";

interface IFileApiClient {
  DownloadHttpFile(fileKey: string): string;
  DownloadHttpZipFile(request: DownloadHttpZipFileRequest): void;
  DownloadHttpFileWithFileName(fileKey: string, fileName: string): string;
  DownloadAsperaFile(fileKey: string): Promise<FileDownloadAsperaResponse>;
  UploadAsperaFile(objectId: number, localFilePaths: string[]): Promise<FileUploadAsperaResponse>;
  UploadAsperaFileTrack(
    objectId: number,
    token: string,
    uuid: string,
    fileNames: string[]
  ): Promise<FileDownloadAsperaResponse>;
  CreateTransferDownloadLink(request: CreateAsperaTransferRequest): Promise<GenericResponseStd>;
  ExpireTransfer(transferId: number): Promise<GenericResponseStd>;
  UpdateTransfer(request: UpdateTransferRequest): Promise<GenericResponseStd>;
  GetFileInfos(fileKey: string): Promise<FileInfosResponse>;
  GetTransferListWithDate(dateIn: string, dateOut: string): Promise<FileTransfersResponse>;
  GetTransferListWithPage(pageNumber: number, pageSize: number): Promise<FileTransfersResponse>;
  GetPlatformDocuments(): Promise<PlatformDocumentsResponse>;
  DownloadPlatformDocument(long: number, authKey: string): string;
  CreatePlatformDocument(
    request: UploadPlatformDocumentRequest,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<GenericResponseStd>;
  UpdatePlatformDocument(
    documentId: number,
    request: UploadPlatformDocumentRequest,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<GenericResponseStd>;
  DeletePlatformDocument(documentId: number): Promise<GenericResponseStd>;
  GetOrderCheckoutAttachedFileTypes(): Promise<GetOrderCheckoutAttachedFileTypesResponse>;
  AttachFileToItem(
    formData: FormData,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<GenericResponseStd>;
  AddDeliveryFileToBasketItem(
    formData: FormData,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<GenericResponseStd>;
  DeleteFileFromItem(basketItemId: number): Promise<GenericResponseStd>;
  DeleteDeliveryFileFromItem(basketItemId: number, fileIndex: number): Promise<GenericResponseStd>;
  GetOrderCheckoutAttachedFileInfos(basketItemId: number): Promise<GetOrderCheckoutAttachedFileInfosResponse>;
  GetOrderDeliveryAttachedFileInfos(basketItemId: number): Promise<GetOrderCheckoutAttachedFileInfosResponse>;
  GetStorageFileListById(storageId: number): Promise<GetStorageFileListResponse>;
  GetStorageFileListByKind(storageKind: StorageKind): Promise<GetStorageFileListResponse>;
  GetPathTreeFileList(request: GetPathFileListRequest): Promise<GetPathFileListRequest>;
}

export default class FileApiClient implements IFileApiClient {
  readonly servicePort = 9510;
  readonly serviceBaseUrl = "file-service/api";

  fileApiClient: IApiClient;

  constructor(fileApiClient: IApiClient, envType: EnvType) {
    this.fileApiClient = fileApiClient;
    if (envType === "local") this.fileApiClient.client.defaults.baseURL += `${this.servicePort}/`;
  }

  DownloadHttpFile(fileKey: string): string {
    return this.fileApiClient.client.defaults.baseURL + `${this.serviceBaseUrl}/download/http/${fileKey}`;
  }
  DownloadHttpFileWithFileName(fileKey: string, fileName: string): string {
    return this.fileApiClient.client.defaults.baseURL + `${this.serviceBaseUrl}/download/http/${fileKey}/${fileName}`;
  }
  DownloadHttpZipFile(params: DownloadHttpZipFileRequest): string {
    return `${this.fileApiClient.client.defaults.baseURL}${this.serviceBaseUrl}/zip?${params.objectIds
      .map((objectId) => `objectIds=${objectId}&`)
      .join("")}includeAllChilds=${params.includeAllChilds}&proxy=${params.proxy}&skipFileIfOffline=${
      params.skipFileIfOffline
    }&password=${params.password}&authKey=${params.authKey}`;
  }

  async DownloadAsperaFile(fileKey: string): Promise<FileDownloadAsperaResponse> {
    return await this.fileApiClient.get(`${this.serviceBaseUrl}/download/aspera/${fileKey}`);
  }
  async UploadAsperaFile(objectId: number, localFilePaths: string[]): Promise<FileUploadAsperaResponse> {
    return await this.fileApiClient.post(`${this.serviceBaseUrl}/upload/aspera`, {
      objectId: objectId,
      sourceLocalFilePaths: localFilePaths,
    });
  }
  async UploadAsperaFileTrack(
    objectId: number,
    token: string,
    uuid: string,
    fileNames: string[]
  ): Promise<FileDownloadAsperaResponse> {
    return await this.fileApiClient.post(`${this.serviceBaseUrl}/upload/aspera-track`, {
      objectId: objectId,
      token: token,
      asperaUuid: uuid,
      fileNames: fileNames,
    });
  }
  async CreateTransferDownloadLink(request: CreateAsperaTransferRequest): Promise<GenericResponseStd> {
    return await this.fileApiClient.post(`${this.serviceBaseUrl}/download/transfer`, request);
  }
  async ExpireTransfer(transferId: number): Promise<GenericResponseStd> {
    return await this.fileApiClient.delete(`${this.serviceBaseUrl}/transfer/expire/${transferId}`);
  }
  async UpdateTransfer(request: UpdateTransferRequest): Promise<GenericResponseStd> {
    return await this.fileApiClient.put(`${this.serviceBaseUrl}/transfer`, request);
  }
  async GetFileInfos(fileKey: string): Promise<FileInfosResponse> {
    return await this.fileApiClient.get(`${this.serviceBaseUrl}/infos/${fileKey}`);
  }
  async GetTransferListWithDate(dateIn: string, dateOut: string): Promise<FileTransfersResponse> {
    return await this.fileApiClient.get(`${this.serviceBaseUrl}/infos/${dateIn}/${dateOut}`);
  }
  async GetTransferListWithPage(pageNumber: number, pageSize: number): Promise<FileTransfersResponse> {
    return await this.fileApiClient.delete(`${this.serviceBaseUrl}/infos/${pageNumber}/${pageSize}`);
  }
  async GetPlatformDocuments(): Promise<PlatformDocumentsResponse> {
    return await this.fileApiClient.get(`${this.serviceBaseUrl}/platform-documents`);
  }
  DownloadPlatformDocument(long: number, authKey: string): string {
    return this.fileApiClient.client.defaults.baseURL + `${this.serviceBaseUrl}/platform-documents/${long}/${authKey}`;
  }
  async CreatePlatformDocument(
    request: UploadPlatformDocumentRequest,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<GenericResponseStd> {
    const formData = new FormData();
    formData.append("file", request.file);
    formData.append("fileType", request.fileType);
    formData.append("description", request.description);
    formData.append("version", request.version);

    return await this.fileApiClient.postUploadFormData(
      `${this.serviceBaseUrl}/platform-documents`,
      formData,
      abortSignal,
      onUploadProgressCallback
    );
  }
  async UpdatePlatformDocument(
    documentId: number,
    request: UploadPlatformDocumentRequest,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<GenericResponseStd> {
    const formData = new FormData();
    formData.append("file", request.file);
    formData.append("fileType", request.fileType);
    formData.append("description", request.description);
    formData.append("version", request.version);

    return await this.fileApiClient.postUploadFormData(
      `${this.serviceBaseUrl}/platform-documents/${documentId}`,
      formData,
      abortSignal,
      onUploadProgressCallback
    );
  }
  async DeletePlatformDocument(documentId: number): Promise<GenericResponseStd> {
    return await this.fileApiClient.delete(`${this.serviceBaseUrl}/platform-documents/${documentId}`);
  }
  async GetOrderCheckoutAttachedFileTypes(): Promise<GetOrderCheckoutAttachedFileTypesResponse> {
    return await this.fileApiClient.get(`${this.serviceBaseUrl}/order/basket/items/file-types`);
  }
  async AttachFileToItem(
    formData: FormData,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<GenericResponseStd> {
    return await this.fileApiClient.postUploadFormData(
      `${this.serviceBaseUrl}/order/basket/items`,
      formData,
      abortSignal,
      onUploadProgressCallback
    );
  }
  async AddDeliveryFileToBasketItem(
    formData: FormData,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<GenericResponseStd> {
    return await this.fileApiClient.postUploadFormData(
      `${this.serviceBaseUrl}/order/basket/items/delivery`,
      formData,
      abortSignal,
      onUploadProgressCallback
    );
  }
  async DeleteFileFromItem(basketItemId: number): Promise<GenericResponseStd> {
    return await this.fileApiClient.delete(`${this.serviceBaseUrl}/order/basket/items/${basketItemId}`);
  }
  async DeleteDeliveryFileFromItem(basketItemId: number, fileIndex: number): Promise<GenericResponseStd> {
    return await this.fileApiClient.delete(
      `${this.serviceBaseUrl}/order/basket/items/delivery/${basketItemId}/${fileIndex}`
    );
  }
  async GetOrderCheckoutAttachedFileInfos(basketItemId: number): Promise<GetOrderCheckoutAttachedFileInfosResponse> {
    return await this.fileApiClient.get(`${this.serviceBaseUrl}/order/basket/items/${basketItemId}`);
  }
  async GetOrderDeliveryAttachedFileInfos(basketItemId: number): Promise<GetOrderCheckoutAttachedFileInfosResponse> {
    return await this.fileApiClient.get(`${this.serviceBaseUrl}/order/basket/items/delivery/${basketItemId}`);
  }
  async GetStorageFileListById(storageId: number): Promise<GetStorageFileListResponse> {
    return await this.fileApiClient.get(`${this.serviceBaseUrl}/files/storages/${storageId}`);
  }
  async GetStorageFileListByKind(storageKind: StorageKind): Promise<GetStorageFileListResponse> {
    return await this.fileApiClient.get(`${this.serviceBaseUrl}/files/storages/kind/${storageKind}`);
  }
  async GetPathTreeFileList(request: GetPathFileListRequest): Promise<GetPathFileListRequest> {
    return await this.fileApiClient.post(`${this.serviceBaseUrl}/files/path-tree`, request);
  }
  async GetFolderSize(objectId: number): Promise<GetFolderOrAssetSizeResponse> {
    return await this.fileApiClient.get(`${this.serviceBaseUrl}/folders/${objectId}/size`);
  }
  async GetAssetSize(objectId: number): Promise<GetFolderOrAssetSizeResponse> {
    return await this.fileApiClient.get(`${this.serviceBaseUrl}/assets/${objectId}/size`);
  }
  //------Utils------
  GetApiUrl(): string {
    return this.fileApiClient.client.defaults.baseURL + this.serviceBaseUrl;
  }
}
